// Export the tour steps so they can be reused in BrandGrandTour
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Welcome steps, parameterised on the portal's display name so a white-label
 * portal greets affiliates with the brand's name instead of "Reveshare"
 * (ticket 4). The name lands inside driver.js HTML, hence the escaping.
 */
export const getAffiliateWelcomeTourSteps = (portalName = "Reveshare") => {
  const safePortalName = escapeHtml(portalName);
  return [
  {
    element: "#welcome-tour",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
          
            <div class="flex -space-x-2">
              <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
                <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
                <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
                </svg>
              </div>
              </div>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-base font-semibold text-foreground">Welcome to ${safePortalName}</span>
          </div>
        </div>`,
      description: `<div class="space-y-3">
          <p class="text-[12px] text-muted-foreground leading-relaxed">Let's get your affiliate program running smoothly! This quick tour will guide you through managing your profile, campaigns, affiliates and their payouts.</p>
          <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
            <div class="flex items-start gap-2">
              <svg class="w-4 h-4 text-brand-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p class="text-xs text-brand-800 font-medium">Complete all sections to start managing your affiliates successfully!</p>
            </div>
          </div>
          <div class="flex items-center gap-2 text-xs text-brand-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Estimated time: 5 minutes</span>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#tour-guide-button",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
          
            <div class="flex -space-x-2">
              <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
                <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
                <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
                </svg>
              </div>
              </div>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-base font-semibold text-foreground">Interactive Tour Guide</span>
          </div>
        </div>`,
      description: `<div class="space-y-3">
          <p class="text-[12px] text-muted-foreground leading-relaxed">This is your personal tour guide button! Hover over it to reveal a comprehensive menu of guided tours that will help you master every aspect of affiliate management.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <div class="flex items-center gap-2 mb-2">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p class="text-xs font-medium text-brand-800">What you'll find here:</p>
            </div>
            <ul class="text-xs text-brand-700 space-y-2">
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Profile Setup Tour - Complete your affiliate profile</span>
              </li>
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Brands and Programs Tour - Manage your programs</span>
              </li>
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Affiliates Payouts Tour - Manage your payouts</span>
              </li>
            </ul>
          </div>
          <div class="flex items-center gap-2 text-xs text-brand-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Each tour is designed to be quick and easy to follow</span>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#about-this-page",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
          
            <div class="flex -space-x-2">
              <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
                <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
                <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
                </svg>
              </div>
              </div>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-base font-semibold text-foreground">About This Page - Your Contextual Helper</span>
          </div>
        </div>`,
      description: `<div class="space-y-3">
          <p class="text-[12px] text-muted-foreground leading-relaxed">This special button provides detailed explanations about whatever page you're currently on. It's like having a personal guide that adapts to your current context!</p>
          <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
            <div class="flex items-start gap-2">
              <svg class="w-4 h-4 text-brand-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
              <div class="flex-1">
                <p class="text-xs font-medium text-brand-800 mb-1">Smart Context Detection</p>
                <p class="text-xs text-brand-700">The system automatically detects which page you're on and provides relevant guidance, tips, and explanations specific to that page's functionality.</p>
              </div>
            </div>
          </div>
          <div class="bg-brand-50 p-3 rounded-lg">
            <div class="flex items-center gap-2 mb-2">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p class="text-xs font-medium text-brand-800">What you'll get:</p>
            </div>
            <ul class="text-xs text-brand-700 space-y-2">
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Step-by-step walkthrough of current page features</span>
              </li>
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Detailed explanations of buttons and functions</span>
              </li>
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Best practices and tips for the current context</span>
              </li>
              <li class="flex items-start gap-2">
                <div class="w-2 h-2 bg-brand-400 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Interactive highlights of important elements</span>
              </li>
            </ul>
          </div>
          <div class="flex items-center gap-2 text-xs text-brand-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Perfect for when you need help with a specific page or feature</span>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  ];
};
